import { supabase } from "@/integrations/supabase/client";

type RequestRow = {
  id: string;
  parent_id: string;
  title: string;
  city: string | null;
  postal_code: string | null;
  proposed_rate: number | null;
};

type ProfileRow = {
  user_id: string;
  full_name: string | null;
  latitude: number | null;
  longitude: number | null;
};

let observer: MutationObserver | null = null;
let scheduled = false;
let requests: RequestRow[] = [];
let profiles = new Map<string, ProfileRow>();
let currentPosition: { lat: number; lng: number } | null = null;

const norm = (v: string | null | undefined) => (v || "").trim().toLocaleLowerCase("fr-FR");
const postalNorm = (v: string | null | undefined) => (v || "").replace(/\s+/g, "").trim();

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

async function loadData() {
  const { data: reqData } = await supabase
    .from("care_requests")
    .select("id,parent_id,title,city,postal_code,proposed_rate")
    .order("created_at", { ascending: false });
  requests = (reqData as RequestRow[] | null) || [];

  const ids = [...new Set(requests.map((r) => r.parent_id))];
  profiles = new Map();
  if (ids.length) {
    const { data: profileData } = await supabase
      .from("profiles")
      .select("user_id,full_name,latitude,longitude")
      .in("user_id", ids);
    (profileData as ProfileRow[] | null)?.forEach((p) => profiles.set(p.user_id, p));
  }
}

function findRequestsCard() {
  const titles = Array.from(document.querySelectorAll<HTMLElement>("h3,div,p"));
  const heading = titles.find((el) => /^Annonces \(\d+\)$/.test((el.textContent || "").trim()));
  if (!heading) return null;
  const card = heading.closest<HTMLElement>("[class*='shadow-md']") || heading.parentElement?.parentElement?.parentElement;
  if (!card) return null;
  return { heading, card };
}

function getRequestCards(card: HTMLElement) {
  return Array.from(card.querySelectorAll<HTMLElement>("div.border.rounded-xl.p-4.cursor-pointer"));
}

function requestForCard(card: HTMLElement) {
  const title = card.querySelector<HTMLElement>("p.font-medium.text-sm")?.textContent?.trim() || "";
  const body = norm(card.textContent);
  return requests.find((r) => {
    if (r.title !== title) return false;
    const profile = profiles.get(r.parent_id);
    const cityOk = !r.city || body.includes(norm(r.city));
    const parentOk = !profile?.full_name || body.includes(norm(profile.full_name));
    return cityOk && parentOk;
  });
}

function buildControls(card: HTMLElement) {
  let controls = document.getElementById("cg-admin-request-filters") as HTMLDivElement | null;
  if (controls) return controls;

  const header = card.querySelector<HTMLElement>("[class*='CardHeader'], .space-y-1\.5") || card.firstElementChild as HTMLElement | null;
  if (!header) return null;

  const cities = Array.from(new Set(requests.map((r) => r.city).filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));

  controls = document.createElement("div");
  controls.id = "cg-admin-request-filters";
  controls.className = "mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2";
  controls.innerHTML = `
    <input data-name type="text" placeholder="Nom du parent" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
    <select data-city class="h-10 rounded-md border border-input bg-background px-3 text-sm">
      <option value="all">Toutes les villes</option>
      ${cities.map((c) => `<option value="${norm(c).replace(/"/g, "&quot;")}">${c}</option>`).join("")}
    </select>
    <input data-postal type="text" inputmode="numeric" maxlength="5" placeholder="Code postal / département" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
    <input data-rate-min type="number" min="0" step="0.5" placeholder="Tarif min €/h" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
    <input data-rate-max type="number" min="0" step="0.5" placeholder="Tarif max €/h" class="h-10 rounded-md border border-input bg-background px-3 text-sm" />
    <select data-radius class="h-10 rounded-md border border-input bg-background px-3 text-sm">
      <option value="off">Géolocalisation désactivée</option>
      <option value="10">Autour de moi · 10 km</option>
      <option value="25">Autour de moi · 25 km</option>
      <option value="50">Autour de moi · 50 km</option>
      <option value="100">Autour de moi · 100 km</option>
    </select>
    <button data-reset type="button" class="h-10 rounded-md border px-3 text-sm font-medium hover:bg-muted">Réinitialiser</button>
    <div data-status class="sm:col-span-2 lg:col-span-7 text-xs text-muted-foreground"></div>
  `;

  header.appendChild(controls);

  const apply = async () => {
    const name = norm((controls!.querySelector<HTMLInputElement>("[data-name]")?.value));
    const city = controls!.querySelector<HTMLSelectElement>("[data-city]")?.value || "all";
    const postal = postalNorm(controls!.querySelector<HTMLInputElement>("[data-postal]")?.value);
    const minRaw = controls!.querySelector<HTMLInputElement>("[data-rate-min]")?.value || "";
    const maxRaw = controls!.querySelector<HTMLInputElement>("[data-rate-max]")?.value || "";
    const min = minRaw === "" ? null : Number(minRaw);
    const max = maxRaw === "" ? null : Number(maxRaw);
    const radiusRaw = controls!.querySelector<HTMLSelectElement>("[data-radius]")?.value || "off";
    const radius = radiusRaw === "off" ? null : Number(radiusRaw);

    if (radius !== null && !currentPosition) {
      const status = controls!.querySelector<HTMLElement>("[data-status]");
      if (status) status.textContent = "Autorisez la localisation pour filtrer autour de vous…";
      try {
        currentPosition = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            reject,
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
          );
        });
      } catch {
        const select = controls!.querySelector<HTMLSelectElement>("[data-radius]");
        if (select) select.value = "off";
        const status = controls!.querySelector<HTMLElement>("[data-status]");
        if (status) status.textContent = "Localisation refusée ou indisponible.";
        return;
      }
    }

    let visible = 0;
    const cards = getRequestCards(card);
    cards.forEach((requestCard) => {
      const req = requestForCard(requestCard);
      if (!req) return;
      const profile = profiles.get(req.parent_id);
      const nameOk = !name || norm(profile?.full_name).includes(name);
      const cityOk = city === "all" || norm(req.city) === city;
      const postalOk = !postal || postalNorm(req.postal_code).startsWith(postal);
      const rate = req.proposed_rate == null ? null : Number(req.proposed_rate);
      const minOk = min == null || (rate != null && rate >= min);
      const maxOk = max == null || (rate != null && rate <= max);
      let geoOk = true;
      if (radius !== null) {
        geoOk = !!currentPosition && profile?.latitude != null && profile?.longitude != null &&
          haversineKm(currentPosition.lat, currentPosition.lng, profile.latitude, profile.longitude) <= radius;
      }
      const show = nameOk && cityOk && postalOk && minOk && maxOk && geoOk;
      requestCard.style.display = show ? "" : "none";
      if (show) visible++;
    });

    const status = controls!.querySelector<HTMLElement>("[data-status]");
    if (status) status.textContent = `${visible} annonce${visible > 1 ? "s" : ""} affichée${visible > 1 ? "s" : ""}`;
  };

  controls.querySelectorAll("input,select").forEach((el) => {
    el.addEventListener(el.tagName === "INPUT" ? "input" : "change", () => { void apply(); });
  });
  controls.querySelector<HTMLButtonElement>("[data-reset]")?.addEventListener("click", () => {
    controls!.querySelectorAll<HTMLInputElement>("input").forEach((i) => { i.value = ""; });
    const city = controls!.querySelector<HTMLSelectElement>("[data-city]");
    const radius = controls!.querySelector<HTMLSelectElement>("[data-radius]");
    if (city) city.value = "all";
    if (radius) radius.value = "off";
    void apply();
  });
  void apply();
  return controls;
}

async function refresh() {
  scheduled = false;
  if (window.location.pathname !== "/admin") {
    document.getElementById("cg-admin-request-filters")?.remove();
    return;
  }
  const found = findRequestsCard();
  if (!found) return;
  if (!requests.length) await loadData();
  buildControls(found.card);
}

function scheduleRefresh() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => { void refresh(); });
}

export function registerAdminRequestFilters() {
  if (typeof window === "undefined" || observer) return;
  const start = () => {
    scheduleRefresh();
    observer = new MutationObserver(scheduleRefresh);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("popstate", scheduleRefresh);
    window.addEventListener("hashchange", scheduleRefresh);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
